import { useMemo } from 'react';
import { CmsOffer } from '../../../../../../types/cmsProduct';
import { useCmsClientContext } from '../../../../../../utils/cmsPageContext';
import { LoanSearchParams } from '../../../../../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../../../utils/translations/tr';
import { formatAmount } from '../../formatAmount';
import { formatOfferReward } from '../../formatOfferReward';
import { getOfferReward } from '../../getOfferReward';
import { TRANSLATIONS } from '../../i18n';
import { ProductDetailsSectionOffersPlSummaryCardProps } from './ProductDetailsSectionOffersPlSummaryCard';
import { getReminder } from './utils/getReminder';

type UseProductDetailsSectionOffersPlSummaryCardParams = {
  productName?: string;
  offers: CmsOffer[];
  loanSearchParams?: LoanSearchParams;
};

// Builds the summary card props from the offers and the page's loan
// selection: each offer's best reward, their total ("Max rewards") and, when
// nothing matches, the reminder in place of the total (CMS-2023 §2.2, §7).
export const useProductDetailsSectionOffersPlSummaryCard = ({
  productName,
  offers,
  loanSearchParams,
}: UseProductDetailsSectionOffersPlSummaryCardParams): Omit<
  ProductDetailsSectionOffersPlSummaryCardProps,
  'onSeeMore'
> => {
  const context = useCmsClientContext();
  const { currencyDisplay } = context;
  const text = getClientTranslations(TRANSLATIONS, context);
  const amount = Number(loanSearchParams?.amount);
  const tenure = Number(loanSearchParams?.tenure);

  return useMemo(() => {
    const rewards = offers.map((offer) => getOfferReward(offer, amount, tenure));
    const hasMatch = rewards.some((reward) => reward.kind !== 'none');
    const totalValue = rewards.reduce(
      (sum, reward) => sum + (reward.kind === 'value' ? reward.value : 0),
      0,
    );
    const reminder = hasMatch
      ? undefined
      : getReminder(offers, amount, tenure, currencyDisplay, text);

    let total: ProductDetailsSectionOffersPlSummaryCardProps['total'];
    if (!hasMatch) {
      total = reminder ? { kind: 'reminder', text: reminder } : { kind: 'none' };
    } else {
      // A reward without a set cash value adds nothing to the total, but it
      // still counts as a reward (Gherkin 10c, 12g).
      total =
        totalValue > 0
          ? { kind: 'value', value: formatAmount(totalValue, currencyDisplay) }
          : { kind: 'noFixedValue' };
    }

    const hasSelection = Number.isFinite(amount) && Number.isFinite(tenure);

    return {
      leadIn:
        hasSelection && productName
          ? tr('lead_in', text, {
              product: productName,
              amount: formatAmount(amount, currencyDisplay),
              count: String(tenure),
            })
          : undefined,
      total,
      offers: offers.map((offer, index) => {
        const reward = rewards[index];
        return {
          key: `${index}-${offer.uid ?? offer.title}`,
          title: offer.offer_title,
          type: offer.type,
          reward: formatOfferReward(reward, currencyDisplay),
        };
      }),
    };
  }, [offers, amount, tenure, productName, currencyDisplay, text]);
};
